// admin/pages/Users.tsx — paginated user table backed by the admin socket.
//
// Reads filter/sort/page from the users slice; on any change, dispatches an
// admin:users_query RPC and stores the result in the slice (only the latest
// requestId wins — older acks are dropped server-side via requestId echo).
// Row actions call admin:toggle_flag and rely on the broadcast leg to surface
// the change to every connected admin's row (no optimistic UI in v1).

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "../styles";
import {
  useAppDispatch,
  useAppSelector,
  usersActions,
} from "../store";
import { rpcToggleFlag, RpcError } from "../socket/rpc";
import type {
  AdminToggleableFlag,
  AdminUser,
  AdminUserSort,
} from "../store/types";

interface UsersPageProps {
  socket: RefObject<Socket | null>;
  /** Current admin's own id — used to disable the toggle-admin row action on their row. */
  viewerUserId: string;
}

const PAGE_SIZES = [25, 50, 100] as const;

interface Column {
  key: AdminUserSort | "actions";
  label: string;
  sortable: boolean;
  width?: number;
  align?: "left" | "right";
}

const COLUMNS: Column[] = [
  { key: "email", label: "Email", sortable: true },
  { key: "name", label: "Name", sortable: true },
  { key: "tier", label: "Tier", sortable: true, width: 90 },
  { key: "subscription_status", label: "Subscription", sortable: true, width: 120 },
  { key: "total_spend_cents", label: "Spend", sortable: true, width: 100, align: "right" },
  { key: "story_count", label: "Stories", sortable: true, width: 80, align: "right" },
  { key: "turn_count", label: "Turns", sortable: true, width: 80, align: "right" },
  { key: "turn_balance", label: "Balance", sortable: true, width: 80, align: "right" },
  { key: "created_at", label: "Joined", sortable: true, width: 110 },
  { key: "actions", label: "", sortable: false, width: 40 },
];

interface Toast {
  id: number;
  text: string;
  error?: boolean;
}

export default function Users({ socket, viewerUserId }: UsersPageProps) {
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.users.query);
  const rows = useAppSelector((s) => s.users.currentPage);
  const total = useAppSelector((s) => s.users.total);
  const loading = useAppSelector((s) => s.users.loading);
  const error = useAppSelector((s) => s.users.error);
  const page = useAppSelector((s) => s.users.page);
  const pageSize = useAppSelector((s) => s.users.pageSize);

  // Transient UI state — filter input (with debounce) + kebab menu state.
  // Filter INPUT is local; the debounced value reaches Redux as query.q.
  const [filterInput, setFilterInput] = useState(query.q);
  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = filterInput.trim();
      if (trimmed !== query.q) {
        dispatch(usersActions.setQuery({ q: trimmed, page: 1 }));
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [filterInput, query.q, dispatch]);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = (text: string, isError = false) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, text, error: isError }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  // ── Query effect: dispatch admin:users_query on every query change ──────
  useEffect(() => {
    const sock = socket.current;
    if (!sock) return;
    let cancelled = false;
    // Generate the requestId inside rpcUsersQuery — but we need to mark the
    // slice as loading with the same id so the ack-matching logic in the
    // reducer can drop stale results. Mint here, pass through.
    const requestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    dispatch(usersActions.requestStarted({ requestId }));
    (async () => {
      try {
        // We use rpcUsersQuery's natural requestId path via a thin custom emit
        // here so the loading-state id matches the wire requestId exactly.
        const result = await new Promise<{
          requestId: string;
          users: AdminUser[];
          total: number;
          page: number;
          pageSize: number;
        }>((resolve, reject) => {
          if (!sock.connected) {
            reject(new RpcError("socket_not_connected"));
            return;
          }
          let settled = false;
          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new RpcError("timeout"));
          }, 15_000);
          sock.emit(
            "admin:users_query",
            {
              requestId,
              q: query.q || undefined,
              sort: query.sort,
              order: query.order,
              page: query.page,
              pageSize: query.pageSize,
            },
            (ack: unknown) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              if (
                ack &&
                typeof ack === "object" &&
                (ack as { ok: boolean }).ok
              ) {
                resolve(ack as {
                  requestId: string;
                  users: AdminUser[];
                  total: number;
                  page: number;
                  pageSize: number;
                });
              } else {
                const e =
                  (ack as { error?: string })?.error ?? "unknown_error";
                reject(new RpcError(e));
              }
            },
          );
        });
        if (cancelled) return;
        dispatch(
          usersActions.requestSucceeded({
            requestId: result.requestId,
            users: result.users,
            total: result.total,
            page: result.page,
            pageSize: result.pageSize,
          }),
        );
      } catch (err) {
        if (cancelled) return;
        dispatch(
          usersActions.requestFailed({
            requestId,
            error: err instanceof Error ? err.message : String(err),
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    socket,
    dispatch,
    query.q,
    query.sort,
    query.order,
    query.page,
    query.pageSize,
  ]);

  // Outside-click closes the open kebab menu.
  useEffect(() => {
    if (!openMenu) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      setOpenMenu(null);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenu]);

  // Any scroll (table wrap, window) or resize would leave the portalled
  // menu floating at a stale viewport position — simplest correct
  // behaviour is to close it. Capture phase catches scrolls in any
  // ancestor (tableWrap has overflow:auto).
  useEffect(() => {
    if (!openMenu) return;
    function close() {
      setOpenMenu(null);
    }
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openMenu]);

  function clickHeader(col: Column) {
    if (!col.sortable) return;
    const next = col.key as AdminUserSort;
    if (next === query.sort) {
      dispatch(
        usersActions.setQuery({
          order: query.order === "asc" ? "desc" : "asc",
          page: 1,
        }),
      );
    } else {
      dispatch(usersActions.setQuery({ sort: next, order: "desc", page: 1 }));
    }
  }

  async function handleToggleFlag(user: AdminUser, flag: AdminToggleableFlag) {
    const sock = socket.current;
    if (!sock) {
      pushToast("Not connected.", true);
      return;
    }
    const current =
      flag === "forever_free"
        ? user.foreverFree
        : flag === "full_scenario_access"
          ? user.fullScenarioAccess
          : user.isAdmin;
    const next = !current;
    setOpenMenu(null);
    // No optimistic UI in v1 — wait for the ack + slice patch.
    try {
      const updated = await rpcToggleFlag(sock, user.id, flag, next);
      dispatch(usersActions.patchRow(updated));
      pushToast(
        `${user.email}: ${flag} ${next ? "ON" : "OFF"}`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "error";
      pushToast(`Toggle failed: ${msg}`, true);
    }
  }

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  return (
    <div>
      <h2 style={styles.pageTitle}>Users</h2>
      <p style={styles.pageSub}>
        {new Intl.NumberFormat("en-US").format(total)} accounts. Toggle tester
        overrides via the row menu.
      </p>

      <div style={styles.controlsRow}>
        <input
          type="text"
          placeholder="Filter by name or email…"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          style={styles.searchInput}
        />
        <label
          style={{
            color: tokens.inkMuted,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          Rows per page
          <select
            value={pageSize}
            onChange={(e) =>
              dispatch(
                usersActions.setQuery({
                  pageSize: Number(e.target.value) as
                    (typeof PAGE_SIZES)[number],
                  page: 1,
                }),
              )
            }
            style={styles.select}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const isActive = col.sortable && query.sort === col.key;
                const thStyle: React.CSSProperties = {
                  ...styles.th,
                  ...(col.sortable ? styles.thSortable : {}),
                  ...(isActive ? styles.thActive : {}),
                  width: col.width,
                  textAlign: col.align ?? "left",
                };
                return (
                  <th
                    key={col.key}
                    style={thStyle}
                    onClick={() => clickHeader(col)}
                  >
                    {col.label}
                    {isActive ? (query.order === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={styles.empty}>
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  style={{ ...styles.empty, color: tokens.destructive }}
                >
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={styles.empty}>
                  No users match.
                </td>
              </tr>
            ) : (
              rows.map((u) => (
                <Row
                  key={u.id}
                  user={u}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  menuRef={menuRef}
                  viewerUserId={viewerUserId}
                  onToggleFlag={handleToggleFlag}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pager}>
        <div>
          Page {page} of {totalPages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() =>
              dispatch(
                usersActions.setQuery({ page: Math.max(1, page - 1) }),
              )
            }
            disabled={page <= 1 || loading}
            style={{
              ...styles.pagerBtn,
              ...(page <= 1 || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() =>
              dispatch(
                usersActions.setQuery({
                  page: Math.min(totalPages, page + 1),
                }),
              )
            }
            disabled={page >= totalPages || loading}
            style={{
              ...styles.pagerBtn,
              ...(page >= totalPages || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Next
          </button>
        </div>
      </div>

      <div style={styles.toastStack}>
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{ ...styles.toast, ...(t.error ? styles.toastError : {}) }}
          >
            {t.text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────

interface RowProps {
  user: AdminUser;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
  menuRef: React.MutableRefObject<HTMLDivElement | null>;
  viewerUserId: string;
  onToggleFlag: (u: AdminUser, flag: AdminToggleableFlag) => void;
}

function Row({
  user,
  openMenu,
  setOpenMenu,
  menuRef,
  viewerUserId,
  onToggleFlag,
}: RowProps) {
  const isMine = user.id === viewerUserId;
  const isOpen = openMenu === user.id;

  return (
    <tr>
      <td style={styles.td}>{user.email}</td>
      <td style={styles.td}>{user.name ?? "—"}</td>
      <td style={styles.td}>
        <span style={styles.pill}>{user.tier}</span>
      </td>
      <td style={styles.td}>
        <SubscriptionPill status={user.subscriptionStatus} />
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, textAlign: "right" }}>
        {user.totalSpendCents > 0
          ? `$${(user.totalSpendCents / 100).toFixed(2)}`
          : "—"}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, textAlign: "right" }}>
        {user.storyCount || "—"}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, textAlign: "right" }}>
        {user.turnCount || "—"}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, textAlign: "right" }}>
        {user.turnBalance}
      </td>
      <td style={{ ...styles.td, ...styles.tdMono, fontSize: 12 }}>
        {new Date(user.createdAt).toISOString().slice(0, 10)}
      </td>
      <td style={styles.td}>
        <RowMenu
          user={user}
          isOpen={isOpen}
          isMine={isMine}
          menuRef={menuRef}
          onToggle={() => setOpenMenu(isOpen ? null : user.id)}
          onToggleFlag={onToggleFlag}
        />
      </td>
    </tr>
  );
}

// RowMenu — kebab button + portalled menu. We portal to document.body so
// the menu escapes the tableWrap's overflow:auto (which otherwise clips it
// to the table's bounds). The menu is fixed-positioned, anchored to the
// kebab button's bounding rect at the moment the menu opens. The parent
// closes the menu on outside-click, scroll, or resize so the cached rect
// never goes stale.
interface RowMenuProps {
  user: AdminUser;
  isOpen: boolean;
  isMine: boolean;
  menuRef: MutableRefObject<HTMLDivElement | null>;
  onToggle: () => void;
  onToggleFlag: (u: AdminUser, flag: AdminToggleableFlag) => void;
}

const MENU_MIN_WIDTH = 240;

function RowMenu({
  user,
  isOpen,
  isMine,
  menuRef,
  onToggle,
  onToggleFlag,
}: RowMenuProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(
    null,
  );

  // Snapshot the button's viewport position at the moment we open. If the
  // menu would overflow the right edge, right-align it to the button
  // instead of letting it hang off-screen.
  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setAnchor(null);
      return;
    }
    const r = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const left = Math.max(
      8,
      Math.min(r.right - MENU_MIN_WIDTH, viewportWidth - MENU_MIN_WIDTH - 8),
    );
    setAnchor({ top: r.bottom + 4, left });
  }, [isOpen]);

  const menuStyle: CSSProperties = {
    ...styles.menu,
    position: "fixed",
    top: anchor?.top ?? 0,
    left: anchor?.left ?? 0,
    right: "auto",
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="row actions"
        style={styles.kebab}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        ⋮
      </button>
      {isOpen && anchor
        ? createPortal(
            <div ref={menuRef} style={menuStyle}>
              <button
                type="button"
                style={styles.menuItem}
                onClick={() => onToggleFlag(user, "forever_free")}
              >
                Forever Free
                <FlagPill on={user.foreverFree} />
              </button>
              <button
                type="button"
                style={styles.menuItem}
                onClick={() => onToggleFlag(user, "full_scenario_access")}
              >
                Full Scenario Access
                <FlagPill on={user.fullScenarioAccess} />
              </button>
              <button
                type="button"
                style={{
                  ...styles.menuItem,
                  ...(user.isAdmin ? styles.menuItemDanger : {}),
                  ...(isMine ? styles.menuItemDisabled : {}),
                }}
                disabled={isMine}
                title={
                  isMine
                    ? "You can't toggle admin on your own account from the portal."
                    : ""
                }
                onClick={() => {
                  if (isMine) return;
                  onToggleFlag(user, "is_admin");
                }}
              >
                Admin
                <FlagPill on={user.isAdmin} />
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function SubscriptionPill({
  status,
}: {
  status: AdminUser["subscriptionStatus"];
}) {
  if (!status) return <span style={{ color: tokens.inkMuted }}>—</span>;
  let style = styles.pillGrey;
  if (status === "active") style = styles.pillActive;
  else if (status === "trialing") style = styles.pillTrial;
  else if (status === "past_due") style = styles.pillWarn;
  return <span style={{ ...styles.pill, ...style }}>{status}</span>;
}

function FlagPill({ on }: { on: boolean }) {
  return (
    <span
      style={{
        ...styles.pill,
        ...(on ? styles.pillActive : styles.pillGrey),
        marginLeft: 12,
      }}
    >
      {on ? "ON" : "OFF"}
    </span>
  );
}
